import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import _, { throttle } from 'lodash';
import { Layout, Button, Spin, Modal, Typography, message } from 'antd';
import screenfull from 'screenfull';
import { LoadingOutlined } from '@ant-design/icons';
import useStores from '@lib/hooks/useStores';
import useVideoContext from '@lib/hooks/useVideoContext';
import Player from '@lib/player';

import { IVideoStore } from '@stores/videoStore';
import { useUIStore } from '@stores/ui';
import { Chapters, IPoseData, ICoords } from '@interfaces/pose.interface';
import { HoveredChapter } from '@interfaces/video.interface';
import { scMixinAbsoluteFull } from '@utils/emotion/mixins';
import SkeletonCanvas from '@components/SkeletonCanvas';
import ChapterBox from '@components/ChapterBox';
import VideoTimeline from '@components/VideoTimeline';
import FrameTimeline from '@components/FrameTimeline';
import axios from 'axios';
import api from '~/lib/api';
import { scFunctionZIndex } from '@utils/emotion/functions';

import { ReactComponent as IconFullscreen } from '@svg/icon-editor-fullscreen.svg';
import { ReactComponent as IconBeta } from '@svg/icon-beta.svg';

const { Content: AntdContent } = Layout;
const { Title, Text } = Typography;

type MatchParams = {
  videoId: string;
};

const CoordsEditor: React.FC = observer(() => {
  const { t } = useTranslation();
  const { videoId } = useParams<keyof MatchParams>();
  const { videoStore } = useStores<{ videoStore: IVideoStore }>();
  const { isSiderCollapsed, toggleSider, setOverrideTheme } = useUIStore();
  const navigate = useNavigate();
  const [isFullscreen, setFullscreen] = useState(false);
  const [{ data, loading, error }] = useVideoContext(videoId);
  const [chapterData, setChapterData] = useState<Chapters>([]);
  const [currentChapterIdx, setCurrentChapterIdx] = useState(0);
  const [currentSec, setCurrentSec] = useState(0);
  const [currentSecFrameIdx, setCurrentSecFrameIdx] = useState(0);
  const [currentFrameData, setCurrentFrameData] = useState<IPoseData | null>(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [closestTime, setClosestTime] = useState(0);
  const flattenedFrameData = useRef<IPoseData[]>([]);
  const groupedFrameData = useRef<any>({});
  const [onSubmitting, setSubmitting] = useState(false);
  const [getFrameAmount] = useState(30);
  const getPoseDataFlagCount = useMemo(() => Math.floor(getFrameAmount * 0.3), [getFrameAmount]);
  // const getPoseDataCancelToken = useRef<any>();
  const [getFrameBeforePoint, setFrameBeforePoint] = useState(0);
  const [getFrameAfterPoint, setFrameAfterPoint] = useState(getFrameAmount);
  const [chapterInfo, setChapterInfo] = useState<Chapters>([]);
  const [isAPICalled, setAPICalled] = useState(false);
  const [isTimeChanged, setTimeChanged] = useState(false);
  const [isMovedRight, setMovedRight] = useState(true);
  const [hoveredChapter, setHoveredChapter] = useState<HoveredChapter>({
    idx: -1,
    from: null,
  });
  const initialSiderCollapsed = useRef(isSiderCollapsed);

  const getPoseData = useCallback(
    async (params?: {
      limit?: number;
      offset?: number;
      order?: 'ASC' | 'DESC';
      frameLimits: string;
      frameOffsets: string;
      frameOrders?: 'ASC' | 'DESC';
    }) => {
      try {
        const poseData = await api.get<any>(`/video/${videoId}/pose`, {
          params,
          // cancelToken: new CancelToken(function executor(c: any) {
          //   getPoseDataCancelToken.current = c;
          // }),
        });
        await poseData.data.data.chapters.forEach((value: any, index: number) => {
          poseData.data.data.chapters[index] = value.data;
        });
        return await poseData.data.data;
      } catch (e) {
        if (axios.isCancel(e)) return;
        console.error('Error at getPoseData', e);
      }
    },
    [videoId],
  );

  const getPoseDataWithOptions = useCallback(
    async (getPoseDataOptions) => {
      //Set frame amount to options.
      getPoseDataOptions.frameLimits = getFrameAmount;
      setFrameBeforePoint(+getPoseDataOptions.frameOffsets);
      setFrameAfterPoint(+getPoseDataOptions.frameOffsets + getFrameAmount);
      //if: check will get data are not exists
      if (
        (!isAPICalled &&
          _.countBy(
            chapterData.find((d) => d.chapterIdx === +getPoseDataOptions.offset)?.poseData,
            (d) =>
              d.frameIdx >= +getPoseDataOptions.frameOffsets &&
              d.frameIdx < +getPoseDataOptions.frameOffsets + getFrameAmount,
          ).true) ||
        0 < getFrameAmount
      ) {
        setAPICalled(true);
        await getPoseData(getPoseDataOptions!).then((response: any) => {
          try {
            // check the loading chapter or frame
            if (chapterData.some((d) => d.chapterIdx === +getPoseDataOptions.offset))
              chapterData.find((d) => d.chapterIdx === +getPoseDataOptions.offset)!.poseData =
                _.sortBy(
                  _.unionBy(
                    response.chapters[0].poseData,
                    chapterData.find((d) => d.chapterIdx === +getPoseDataOptions.offset)?.poseData,
                    'frameIdx',
                  ),
                  'frameIdx',
                );
            else {
              chapterData.push(response.chapters[0]);
              setChapterData(_.sortBy(chapterData, 'chapterIdx'));
            }

            flattenedFrameData.current = _.chain(chapterData)
              .flatMap((c) => c.poseData!)
              .value();

            groupedFrameData.current = _.chain(chapterData)
              .flatMap((c) => c.poseData)
              .groupBy((p) => Math.floor(p!.time))
              .map((d) => _.keyBy(d, 'time'))
              .mapKeys((d) => Math.floor(Number(Object.keys(d)[0])))
              .value();

            setAPICalled(false);
          } catch {}
        });
      }
    },
    [chapterData, getFrameAmount, getPoseData, isAPICalled],
  );

  const fullscreenCallback = () => {
    if (!screenfull.isEnabled) return;

    if (screenfull.isFullscreen) {
      setFullscreen(true);
      toggleSider(true);
    } else {
      setFullscreen(false);
      toggleSider(initialSiderCollapsed.current);
    }
  };

  useEffect(() => {
    if (screenfull.isEnabled) {
      screenfull.on('change', fullscreenCallback);
    }

    return () => {
      if (screenfull.isEnabled) {
        screenfull.off('change', fullscreenCallback);
        screenfull.exit();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOverrideTheme('dark');

    return () => {
      // if (getPoseDataCancelToken.current) {
      //   getPoseDataCancelToken.current();
      // }
      setOverrideTheme(null);
      videoStore.resetStore();
    };
  }, [setOverrideTheme, videoStore]);

  const poseSubmit = useCallback(() => {
    const submitCoords: ICoords = [];
    chapterData.forEach((cv) => {
      cv.poseData!.forEach((fv) => {
        if (
          fv.data.keypoints.some((kv, kIndex) =>
            kIndex > 5 && kv.hasOwnProperty('isNew') ? _.unset(kv, 'isNew') : false,
          )
        ) {
          if (!submitCoords.some((sv) => sv.chapterIdx === cv.chapterIdx)) {
            // add Chapter
            submitCoords.push({
              chapterIdx: cv.chapterIdx,
              poseData: [],
            });
          }
          // add Frame
          submitCoords[submitCoords.findIndex((i) => i.chapterIdx === cv.chapterIdx)].poseData.push(
            {
              frameIdx: fv.frameIdx,
              data: fv.data,
            },
          );
        }
      });
    });
    setSubmitting(true);
    api
      .patch(`/video/${videoId}/pose`, submitCoords)
      .then(() => {
        message.success(t('video.editor.coords.form.submit.success'), 4);
        setSubmitting(false);
        setIsUpdated(false);
      })
      .catch(() => {
        setSubmitting(false);
        Modal.error({
          title: t('error.api.common.submit.server.title'),
          content: t('error.api.coordseditor.failed.submit'),
          okText: t('a11y.ok'),
        });
      });
  }, [chapterData, t, videoId]);

  useEffect(() => {
    if (loading) return;

    if (!loading && !data) {
      Modal.error({
        title: t('error.api.common.retrieve.server.title'),
        content: t('error.api.coordseditor.failed'),
        okText: t('error.back'),
        onOk() {
          navigate(-1);
        },
      });

      return;
    }

    if (!loading && !error && data) {
      if (data.jobs.transcoding === 'processing') {
        Modal.warn({
          title: t('error.api.common.retrieve.server.title'),
          content: t('error.api.edit.ontranscoding'),
          okText: t('error.back'),
          onOk: () => {
            navigate(-1);
          },
        });

        return;
      }

      if (data.type === 'explanation') {
        Modal.warn({
          title: t('video.common.warn.title'),
          content: t('video.common.warn.editor.explanation.coords'),
          okText: t('error.back'),
          onOk: () => {
            navigate(-1);
          },
        });

        return;
      }

      if (!data.pose.hasOwnProperty('chapters')) {
        Modal.error({
          title: t('error.api.common.retrieve.server.title'),
          content: t('error.api.coordseditor.failed'),
          okText: t('error.back'),
          onOk() {
            navigate(-1);
          },
        });

        return;
      }

      if (data.jobs.poseEstimation !== 'complete') {
        Modal.error({
          title: t('error.api.common.retrieve.server.title'),
          content: t('error.api.coordseditor.failed.processing'),
          okText: t('error.back'),
          onOk() {
            navigate(-1);
          },
        });

        return;
      }

      const { title } = data;
      if (!chapterData.length && !chapterInfo.length) {
        setChapterInfo(data.pose.chapters);
        // first pose data load
        getPoseData({
          limit: 1,
          frameLimits: `${getFrameAmount}`,
          frameOffsets: `0`,
        }).then((response) => {
          if (!response) return;

          const pose = response;

          videoStore.setVideoData({ videoId, title });
          if (pose.state === 'complete' && pose.chapters.length) {
            flattenedFrameData.current = _.chain(pose.chapters!)
              .flatMap((c) => c.poseData!)
              .value();

            groupedFrameData.current = _.chain(pose.chapters)
              .flatMap((c) => c.poseData)
              .groupBy((p) => Math.floor(p!.time))
              .map((d) => _.keyBy(d, 'time'))
              .mapKeys((d) => Math.floor(Number(Object.keys(d)[0])))
              .value();

            setChapterData([...pose.chapters]);
          }
        });
      }
    }
  }, [
    chapterData,
    currentChapterIdx,
    data,
    error,
    getFrameAmount,
    getPoseDataFlagCount,
    getPoseData,
    navigate,
    loading,
    t,
    videoId,
    videoStore,
    chapterInfo,
    getPoseDataWithOptions,
    isAPICalled,
    isTimeChanged,
    getFrameBeforePoint,
    getFrameAfterPoint,
  ]);

  const toggleFullscreen = () => {
    if (!screenfull.isEnabled) return;

    screenfull.toggle();
  };

  const shortcutHandler = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const fd = groupedFrameData.current;
      if (videoStore.playerRef && (target.nodeName === 'BODY' || target.nodeName === 'VIDEO')) {
        const { playerRef } = videoStore;
        const c = _.find(chapterInfo, (data) => data.chapterIdx === currentChapterIdx);
        const cStartTime = c!.startTime;
        const cEndTime = c!.endTime;
        const nCurrentSec = Math.floor(playerRef.currentTime());
        const prevSec = (nCurrentSec - 1).toString();
        const nextSec = (nCurrentSec + 1).toString();
        const videoSecKeys = Object.keys(fd);

        try {
          const frameKeys = Object.keys(fd[nCurrentSec]);
          const currentSecFrameIndex = frameKeys.indexOf(closestTime.toString());
          let prevFrameTime, nextFrameTime;
          if (currentSecFrameIndex === 0 && frameKeys.length === 1) {
            if (fd[nCurrentSec][frameKeys[currentSecFrameIndex]].time % 1 > 0.5) {
              const targetSecKeys = Object.keys(fd[nextSec]);
              const firstFrameTimeKey = targetSecKeys[0];
              nextFrameTime = fd[nextSec][firstFrameTimeKey].time;
            } else {
              const targetSecKeys = Object.keys(fd[prevSec]);
              const lastFrameTimeKey = targetSecKeys[targetSecKeys.length - 1];
              prevFrameTime = fd[prevSec][lastFrameTimeKey].time;
            }
          } else if (currentSecFrameIndex === 0) {
            if (videoSecKeys.includes(prevSec)) {
              const targetSecKeys = Object.keys(fd[prevSec]);
              const lastFrameTimeKey = targetSecKeys[targetSecKeys.length - 1];

              prevFrameTime = fd[prevSec][lastFrameTimeKey].time;
              nextFrameTime = fd[nCurrentSec][frameKeys[currentSecFrameIndex + 1]].time;
            } else {
              nextFrameTime = fd[nCurrentSec][frameKeys[currentSecFrameIndex + 1]].time;
            }
          } else if (currentSecFrameIndex === frameKeys.length - 1) {
            if (videoSecKeys.includes(nextSec)) {
              const targetSecKeys = Object.keys(fd[nextSec]);
              const firstFrameTimeKey = targetSecKeys[0];

              prevFrameTime = fd[nCurrentSec][frameKeys[currentSecFrameIndex - 1]].time;
              nextFrameTime = fd[nextSec][firstFrameTimeKey].time;
            } else {
              prevFrameTime = fd[nCurrentSec][frameKeys[currentSecFrameIndex - 1]].time;
            }
          } else {
            //Sometimes get error by can't find time property at set data prevFrameTime
            prevFrameTime =
              fd[nCurrentSec][frameKeys[currentSecFrameIndex - 1]].time ||
              fd[nCurrentSec][frameKeys[currentSecFrameIndex]].time;
            nextFrameTime = fd[nCurrentSec][frameKeys[currentSecFrameIndex + 1]].time;
          }

          switch (e.keyCode) {
            case 70: // F: Fullscreen
              toggleFullscreen();
              return;
            // case 32: // SPACE: Play/Pause
            //   e.preventDefault();
            // if (playerRef.paused()) {
            //   if (playerRef.currentTime() < c!.endTime && playerRef.currentTime() < cLastFrameTime) {
            //     playerRef.play();
            //   }
            // } else {
            //   playerRef.pause();
            // }
            // break;
            case 37: // LEFT ARROW: -1 frame move
              if (playerRef.paused()) {
                if (e.shiftKey) {
                  e.preventDefault();
                  setFrameBeforePoint(0);
                  setFrameAfterPoint(getFrameAmount);
                  if (currentChapterIdx > 0) {
                    setCurrentChapterIdx(currentChapterIdx - 1);
                  } else {
                    setCurrentChapterIdx(chapterData.length - 1);
                  }
                } else {
                  if (prevFrameTime > 0 && cStartTime < prevFrameTime) {
                    setMovedRight(false);
                    playerRef.currentTime(prevFrameTime);
                  }
                }
              }
              break;
            case 39: // RIGHT ARROW: +1 frame move
              if (playerRef.paused()) {
                if (e.shiftKey) {
                  e.preventDefault();
                  setFrameBeforePoint(0);
                  setFrameAfterPoint(getFrameAmount);
                  if (chapterInfo.length > currentChapterIdx + 1) {
                    setCurrentChapterIdx(currentChapterIdx + 1);
                  } else {
                    setCurrentChapterIdx(0);
                  }
                } else {
                  if (nextFrameTime > 0 && cEndTime > nextFrameTime) {
                    setMovedRight(true);
                    playerRef.currentTime(nextFrameTime);
                  }
                }
              }
              break;
            default:
          }
        } catch (e) {
          // console.error('Error at shortcutHandler', e);
        }
      }
    },
    [chapterData.length, chapterInfo, closestTime, currentChapterIdx, getFrameAmount, videoStore],
  );

  const throttledShortcutHandler = throttle(shortcutHandler, 125, {
    leading: false,
  });

  useEffect(() => {
    window.addEventListener('keydown', throttledShortcutHandler);

    if (isAPICalled || isTimeChanged)
      window.removeEventListener('keydown', throttledShortcutHandler);
    else window.addEventListener('keydown', throttledShortcutHandler);

    return () => {
      window.removeEventListener('keydown', throttledShortcutHandler);
    };
  }, [isAPICalled, isTimeChanged, throttledShortcutHandler]);

  const setCurrentFrame = useCallback(
    (time: number) => {
      videoStore.playerRef!.currentTime(time);
    },
    [videoStore],
  );

  useEffect(() => {
    if (!loading && chapterData.length && !isAPICalled && !isTimeChanged) {
      const { currentTime } = videoStore.playerData;
      const currentSec = Math.floor(currentTime);
      const currentChapterSecKeys = Object.keys(groupedFrameData.current);
      if (currentChapterSecKeys.includes(currentSec.toString())) {
        const secFrameTimeKeys = Object.keys(groupedFrameData.current[currentSec]).map((x) => +x);
        const closest = secFrameTimeKeys.reduce((p: number, c: number) =>
          //sometimes get error by P or C going to value 0, so check the if is not 0
          p !== 0 && c !== 0 && Math.abs(c - currentTime) < Math.abs(p - currentTime) ? c : p,
        );
        let secFrameIndex = 0;
        if (secFrameTimeKeys.length < 8) {
          secFrameIndex =
            secFrameTimeKeys[0] % 1 >= 1 - (secFrameTimeKeys[secFrameTimeKeys.length - 1] % 1)
              ? secFrameTimeKeys.indexOf(closest) + Math.abs(secFrameTimeKeys.length - 8) + 1
              : secFrameTimeKeys.indexOf(closest) + 1;
        } else {
          secFrameIndex = secFrameTimeKeys.indexOf(closest) + 1;
        }

        setClosestTime(Number(closest));
        setCurrentSecFrameIdx(secFrameIndex);

        setCurrentFrameData(groupedFrameData.current[currentSec][closest]);
        /* Dynamically get pose data when event */
        // pre loading when after chapter moved and check same chapter idx for call once at the same time
        if (
          currentFrameData &&
          currentFrameData.frameIdx < getFrameAfterPoint &&
          currentFrameData.frameIdx > getFrameAfterPoint - getPoseDataFlagCount &&
          isMovedRight
        ) {
          // pre loading when need after frame
          getPoseDataWithOptions({
            limit: 1,
            offset: currentChapterIdx,
            frameLimits: `${getFrameAmount}`,
            frameOffsets: `${getFrameAfterPoint}`,
          });
        } else if (
          currentFrameData &&
          currentFrameData.frameIdx > getFrameBeforePoint &&
          currentFrameData.frameIdx < getFrameBeforePoint + getPoseDataFlagCount &&
          getFrameBeforePoint - getFrameAmount > 0 &&
          !isMovedRight
        ) {
          // pre loading when need before frame
          getPoseDataWithOptions({
            limit: 1,
            offset: currentChapterIdx,
            frameLimits: `${getFrameAmount}`,
            frameOffsets: `${getFrameBeforePoint - getFrameAmount}`,
          });
        } else if (
          (currentFrameData &&
            currentFrameData.frameIdx <
              (getFrameBeforePoint - getPoseDataFlagCount < 0
                ? 0
                : getFrameBeforePoint - getPoseDataFlagCount)) ||
          (currentFrameData &&
            currentFrameData.frameIdx > getFrameAfterPoint + getPoseDataFlagCount)
        ) {
          // When the click the frame if data pose is exsists
          // have to set BeforeFrameIndex and AfterFrameIndex
          const availableFrameCheck =
            currentFrameData!.frameIdx - getFrameAmount / 2 > getFrameAmount;
          setFrameBeforePoint(
            availableFrameCheck ? currentFrameData.frameIdx - getFrameAmount / 2 : 0,
          );
          setFrameAfterPoint(
            availableFrameCheck ? currentFrameData.frameIdx + getFrameAmount / 2 : getFrameAmount,
          );
        }
      } else {
        setTimeChanged(true);
        /* get pose data based Current frame idx */
        // get before & after frame at the current frame

        getPoseDataWithOptions({
          limit: 1,
          offset: currentChapterIdx,
          frameLimits: `${getFrameAmount}`,
          frameOffsets: `${Math.floor(
            (currentSec - chapterInfo.find((d) => d.chapterIdx === currentChapterIdx)!.startTime) *
              8 -
              getFrameAmount / 2,
          )}`,
        })
          .then(() => {
            if (groupedFrameData.current) {
              let currentSecond =
                currentSec === 0
                  ? Math.floor(
                      +Object.keys(
                        groupedFrameData.current[Object.keys(groupedFrameData.current)[0]],
                      )[0],
                    )
                  : currentSec;
              const secFrameTimeKeys =
                groupedFrameData.current[currentSecond] === null ||
                groupedFrameData.current[currentSecond] === undefined
                  ? Object.keys(groupedFrameData.current[--currentSecond]).map((x) => +x)
                  : Object.keys(groupedFrameData.current[currentSecond]).map((x) => +x);
              const closest = secFrameTimeKeys.reduce((p: number, c: number) =>
                Math.abs(c - currentTime) < Math.abs(p - currentTime) ? c : p,
              );

              setCurrentFrame(closest);

              setCurrentFrameData(groupedFrameData.current[currentSecond][closest]);
              setTimeChanged(false);
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    }
  }, [
    loading,
    chapterData,
    videoStore.playerData,
    videoStore.playerRef,
    currentFrameData,
    getPoseData,
    currentChapterIdx,
    data,
    currentSecFrameIdx,
    currentSec,
    getPoseDataFlagCount,
    getFrameAmount,
    getFrameAfterPoint,
    getFrameBeforePoint,
    chapterInfo,
    getPoseDataWithOptions,
    isAPICalled,
    isTimeChanged,
    videoStore,
    closestTime,
    isMovedRight,
    isUpdated,
    setCurrentFrame,
  ]);

  useEffect(() => {
    if (
      !loading &&
      chapterData.length &&
      chapterData.some((d) => d.chapterIdx === currentChapterIdx)
    ) {
      const c = _.find(chapterData, (data) => data.chapterIdx === currentChapterIdx);
      const startTime = c!.startTime;
      const endTime = c!.poseData![c!.poseData!.length - 1].time;
      videoStore.setPlayerData({ range: { startTime, endTime } });
    }
  }, [loading, chapterData, currentChapterIdx, videoStore, currentFrameData]);

  useEffect(() => {
    if (currentSec !== Math.floor(videoStore.playerData.currentTime)) {
      setCurrentSec(Math.floor(videoStore.playerData.currentTime));
    }
  }, [videoStore.playerData, currentSec]);

  const setCurrentChapterIdxCallback = (idx: number) => {
    setFrameBeforePoint(0);
    setFrameAfterPoint(getFrameAmount);
    setCurrentChapterIdx(idx);
  };

  if (loading || error || !data) {
    return (
      <Content>
        <div className="loading-screen">
          <div className="dimmer" />
          <div className="inner">
            <Spin
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 48,
                    color: 'rgb(255, 255, 255, 0.75)',
                  }}
                />
              }
            />
          </div>
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <HeaderBlock fullscreen={isFullscreen} key="header">
        <Typography>
          <Title>
            {t('videos.action.edit.coords.title')}
            <i className="icon" aria-label="Beta">
              <IconBeta />
            </i>
          </Title>
          <Text>{t('videos.action.edit.coords.desc')}</Text>
        </Typography>
        <div className="util-area">
          {/* <Tooltip placement="left" title={`${t('a11y.fullscreen')} (f)`}> */}
          <button
            className="btn-fullscreen"
            aria-label="Fullscreen"
            onClick={() => toggleFullscreen()}
          >
            <IconFullscreen />
          </button>
          {/* </Tooltip> */}
        </div>
      </HeaderBlock>
      <EditorBlock fullscreen={isFullscreen} key="editor">
        <div className="video-area">
          <div className="screen-box">
            <div className="screen-box-inner">
              <div className="player-area">
                <Player
                  controls
                  inactivityTimeout={0}
                  controlBar={{
                    children: [
                      'playToggle',
                      'customControlSpacer',
                      'playbackRateMenuButton',
                      'volumePanel',
                      'fullscreenToggle',
                    ],
                  }}
                >
                  <source
                    src={data.transcodings[data.apiVersion === 'v1' ? 1 : 0].url}
                    type={data.apiVersion === 'v1' ? 'video/mp4' : 'application/x-mpegURL'}
                  />
                </Player>
                <SkeletonCanvas
                  type="screen"
                  currentFrameData={currentFrameData}
                  setCurrentFrameData={setCurrentFrameData}
                  setIsUpdated={setIsUpdated}
                />
              </div>
              <div className="toolbar">
                <div className="guide">
                  <i className="joint joint-left" />
                  <i className="joint joint-right" />
                  <span className="text">{t('video.editor.coords.player.guide.drag')}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="chapter-box">
            <ChapterBox
              editable={false}
              isFullscreen={isFullscreen}
              duration={videoStore.playerData.duration}
              currentTime={videoStore.playerData.currentTime}
              chapterData={chapterInfo}
              onChapterClick={setCurrentChapterIdxCallback}
              currentChapterIdx={currentChapterIdx}
              hoveredChapter={hoveredChapter}
              onChapterMouseOver={(data: HoveredChapter) => setHoveredChapter(data)}
            />
          </div>
        </div>
        <div className="timeline-area">
          <VideoTimeline
            type="coords"
            editable={false}
            duration={videoStore.playerData.duration}
            currentTime={videoStore.playerData.currentTime}
            currentProgress={videoStore.currentProgress}
            currentChapterIdx={currentChapterIdx}
            currentSecFrameIdx={currentSecFrameIdx}
            chapterData={chapterInfo}
            closestTime={closestTime}
            hoveredChapter={hoveredChapter}
            onChapterMouseOver={(data: HoveredChapter) => setHoveredChapter(data)}
          />

          <FrameTimeline
            duration={videoStore.playerData.duration}
            flattenedFrameData={_.filter(
              flattenedFrameData.current,
              (d) =>
                d.time > chapterInfo.find((d) => d.chapterIdx === currentChapterIdx)!.startTime &&
                d.time < chapterInfo.find((d) => d.chapterIdx === currentChapterIdx)!.endTime,
            )}
            closestTime={closestTime}
            setCurrentFrame={setCurrentFrame}
          />
        </div>
      </EditorBlock>
      <FooterBlock fullscreen={isFullscreen} key="footer">
        <div className="action-area">
          <Button
            type="primary"
            size="large"
            onClick={() => poseSubmit()}
            disabled={!isUpdated}
            loading={onSubmitting}
            style={{
              width: 'auto',
              minWidth: 160,
            }}
          >
            {t('videos.action.edit.coords.save')}
          </Button>
        </div>
      </FooterBlock>
    </Content>
  );
});

const Content = styled(AntdContent)`
  display: flex;
  flex-direction: column;
  position: relative;

  .loading-screen {
    display: flex;
    justify-content: center;
    align-items: center;
    ${scMixinAbsoluteFull};
    ${scFunctionZIndex('loading')};

    .dimmer {
      ${scMixinAbsoluteFull};
      z-index: 0;
      background-color: rgba(0, 0, 0, 0.6);
    }

    .inner {
      position: relative;
      z-index: 1;
    }
  }
`;

interface StyledHeaderBlockProps {
  fullscreen: boolean;
}

const HeaderBlock = styled.div<StyledHeaderBlockProps>`
  display: block;
  position: relative;
  padding: ${({ fullscreen }) => (fullscreen ? '18px 36px 0 22px' : '18px 36px 30px')};

  .action-area {
    .ant-btn {
      float: right;
      width: 160px;
    }
  }

  & > article.ant-typography {
    overflow: hidden;
    height: ${({ fullscreen }) => (fullscreen ? '0' : 'auto')};
    margin-bottom: ${({ fullscreen }) => (fullscreen ? '0' : '16px')};
  }

  h1.ant-typography {
    font-weight: bold;
    font-size: 23px;
    line-height: 1.5;
    letter-spacing: 0.01em;

    .icon {
      display: inline-block;
      position: relative;
      top: -6px;
      margin-left: 8px;
    }
  }

  span.ant-typography {
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: 0.01em;
    white-space: pre;
    color: #b6b7b8;
  }

  .util-area {
    position: absolute;
    right: 48px;
    bottom: -26px;
  }

  .btn-fullscreen {
    display: block;
    margin: -6px;
    padding: 6px;
    line-height: 1;
    cursor: pointer;

    svg {
      display: block;
    }
  }
`;

interface StyledEditorBlockProps {
  fullscreen: boolean;
}

const EditorBlock = styled.div<StyledEditorBlockProps>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;

  .video-area {
    flex: 40vh;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    margin: ${({ fullscreen }) => (fullscreen ? '0 36px 0 22px' : '0 36px 0 0')};
    border-color: #4f4f4f;
    border-style: solid;
    border-width: 0;
    border-left-width: ${({ fullscreen }) => (fullscreen ? '1px' : '0')};

    .screen-box {
      flex: 1 1 auto;
      position: relative;
      border-top: 1px solid #4f4f4f;
      box-sizing: border-box;
      background-color: #090909;
    }

    .screen-box-inner {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .player-area {
      flex: 1;
      position: relative;
    }

    .chapter-box {
      flex: 0 0 256px;
      box-sizing: border-box;
    }

    .toolbar {
      border-color: #424242;
      border-style: solid;
      border-top-width: 1px;
      border-right-width: 0;
      border-bottom-width: 1px;
      border-left-width: 0;

      .guide {
        float: right;
        padding: 4px 14px 4px 0;
        line-height: 1;

        .joint {
          display: inline-block;
          width: 8px;
          height: 8px;
          margin-left: 4px;
          border: 1px solid #fcfcfc;
          border-radius: 50%;
          line-height: 1;

          &.joint-left {
            background-color: #1860cc;
          }

          &.joint-right {
            background-color: #cc1937;
          }
        }

        .text {
          display: inline-block;
          margin-left: 4px;
          font-weight: bold;
          font-size: 12px;
          line-height: 1.5;
          color: #b6b7b8;
        }
      }
    }
  }

  .timeline-area {
    flex: 0 0 180px;
    padding: ${({ fullscreen }) => (fullscreen ? '0 36px 0 22px' : '0 36px')};
  }

  .video-js {
    width: auto;
    height: 100%;

    .vjs-big-play-button {
      display: none;
    }
  }
`;

interface StyledFooterBlockProps {
  fullscreen: boolean;
}

const FooterBlock = styled.div<StyledFooterBlockProps>`
  display: block;
  min-height: 128px;
  padding: ${({ fullscreen }) => (fullscreen ? '18px 36px 62px' : '18px 36px 72px')};

  .action-area {
    .ant-btn {
      float: right;
      width: 160px;
    }
  }
`;

export default CoordsEditor;
